import { useAppDispatch } from "@app/hooks"
import { validateCourseForPreview } from "@features/courseEditor/courseEditorSlice"
import { discardDraft } from "@features/coursesMenu/coursesMenuSlice"
import { PopupState, PopupType, popupItemClicked } from "@features/ui/uiSlice"
import { CourseStatus } from "@masterschool/course-builder-api"
import { useNavigate } from "react-router-dom"
import { useEditCourseClicked } from "../../../editor/custom-hooks/useEditCourseClicked"
import { parseStringVersion } from "@utils/versionUtils"
import { CourseMajorDescriptorDto } from "../utils"

function useCourseMajorsMenuActions(course: CourseMajorDescriptorDto) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { courseId, status, version } = course
  const { major: majorVersion } = parseStringVersion(version)

  const onOpenClicked = () => {
    if (course.status === CourseStatus.Draft) {
      navigate(`/course-edit/${courseId}?majorVersion=${majorVersion}`)
    } else {
      navigate(`/course/${courseId}?version=${course.version}`)
    }
  }

  const onPreviewInCampusClicked = () => {
    const popupModel: PopupState = {
      course,
      type: PopupType.CampusCoursePreview,
    }
    switch (status) {
      case CourseStatus.Published:
        dispatch(popupItemClicked(popupModel))
        break
      case CourseStatus.Draft:
        dispatch(validateCourseForPreview({ courseId, majorVersion })).then(
          (flattenedValidations) => {
            const isCourseValid =
              Object.keys(flattenedValidations.payload ?? {}).length === 0
            if (isCourseValid) {
              dispatch(popupItemClicked(popupModel))
            }
          },
        )
        break
      case CourseStatus.Archived:
        throw new Error("Course is archived, cannot preview")
    }
  }

  const onEditClicked = useEditCourseClicked()

  const onVersionHistoryClicked = () => {
    dispatch(
      popupItemClicked({
        courseId,
        majorVersion,
        type: PopupType.CourseVersionsHistory,
        context: "courseEditor",
      }),
    )
  }

  const onDiscardDraftVersionClicked = () => {
    dispatch(discardDraft({ courseId, version }))
  }

  return {
    onOpenClicked,
    onPreviewInCampusClicked,
    onEditClicked,
    onDiscardDraftVersionClicked,
    onVersionHistoryClicked,
  }
}

export default useCourseMajorsMenuActions
